/** @jsx isolateComponent */

import type { Interpolation } from '@emotion/core';

import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { Link } from '../../navigation/Link';
import { Colors, Typography } from '../../../theme';

export type Props = {
  /**
   * Optional label for the menu item.
   */
  ariaLabel?: string;
  /**
   * What to render in the `MenuItem`.
   */
  // What to render within `MenuItem`.
  children: React.ReactNode;
  /**
   * Indicates whether the item should be disabled
   */
  disabled?: boolean;
  /**
   * Optional property which, when supplied, will render `MenuItem` as an `a` tag.
   */
  href?: string;
  /**
   * Whether the item is currently selected. Should be used in circumstances
   * where items are ephemerally chosen.
   */
  isSelected?: boolean;
  /**
   * Optional `id` so the node can be referenced.
   */
  id?: string;
  /**
   * The a11y role for the item.
   */
  role?: 'option';
  /**
   * Optional click handler for the `MenuItem`.
   */
  onClick?: (event: React.MouseEvent) => void;
};

const Styles = labelStyles('MenuItem', {
  content: (props: Props) => [
    {
      ...Typography.altText900,
      alignItems: 'center',
      borderRadius: 10,
      display: 'flex',
      height: 40,
      lineHeight: '40px',
      padding: '0 10px',
      width: '100%',

      // Bump specificity with `[class]` since these states
      // should have precedence over `isSelected`.
      '&:not([disabled]):hover, &:not([disabled]):focus': {
        backgroundColor: Colors.blue100,
        color: Colors.blue500,
        fontWeight: 600,
      },

      '&[disabled]': {
        color: Colors.grey500,
        cursor: 'not-allowed',
      },
    },

    props.isSelected &&
      withModifier('selected', {
        backgroundColor: Colors.grey100,
      }),
  ],

  text: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

export const MenuItem = (props: Props) => {
  const Tag = props.href && !props.disabled ? Link : 'button';
  const isLink = Tag === Link;
  const ariaLabel = props.isSelected
    ? `${props.ariaLabel || props.children}, selected`
    : props.ariaLabel;
  const conditionalProps = isLink
    ? {
        ariaLabel,
        href: props.href,
      }
    : {
        'aria-label': ariaLabel,
        type: 'button' as 'button',
        disabled: props.disabled,
      };
  return (
    <li>
      <Tag
        {...conditionalProps}
        // @ts-ignore doesn't understand Link[color=null]
        color={isLink ? null : undefined}
        css={Styles.content(props) as Interpolation}
        href={props.href}
        onClick={props.onClick}
      >
        <span css={Styles.text as Interpolation}>{props.children}</span>
      </Tag>
    </li>
  );
};
